import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Col, Input, Form, Select, Button } from 'antd';
import { useIsClientAdmin } from 'vl-common/src/hooks/useIsClientAdmin';
import useApi from 'vl-common/src/hooks/useApi';
import { useQuery, useMutation } from '@tanstack/react-query';
import { AlertMessage } from 'vl-common/src/components/AlertMessage/AlertMessage';
import { Task } from 'vl-common/src/types';
import { Datestamp } from 'vl-common/src/components/Datestamp';

interface EditTaskModalProps {
  show: boolean;
  selectedTask: Task | undefined;
  onClose: () => void;
  onCompleted: () => void;
}

const EditTaskModalContent = ({ selectedTask, onClose, onCompleted }: Omit<EditTaskModalProps, 'show'>) => {
  const { t } = useTranslation('common'); // Note:
  // - This component replicates the edit pop-up from the task actions on vl-admin, it currently only updates the assigned_to field.
  // - The component is set up to (in the future) be able to update more, but the mutation function currently only updates the assigned_to field.
  // - It is only useable by client admins.
  const { Api } = useApi();
  const [form] = Form.useForm();
  const isClientAdmin = useIsClientAdmin();
  const [assignedTo, setAssignedTo] = useState('');
  const [showAlertMessage, setShowAlertMessage] = useState({
    visibility: false,
    msg: ''
  });

  const { data: taskAssignee, isLoading: isTaskAssigneeLoading } = useQuery({
    queryKey: ['taskAssignee'],
    queryFn: async () => await Api.task.getTaskAssignee({
      page: 1,
      rows: 999,
      sort: 'fname',
      order: 'asc'
    })
  });

  const { mutate: updateTask, isPending: isUpdateTaskLoading } = useMutation({
    mutationKey: ['updateAssignedTo'],
    mutationFn: async () => await Api.task.reassign({
      task_id: selectedTask?.task_id,
      assignee_user_guid: assignedTo
    }),
    onError: () => displayAlertMessage('Bad Request, Try again.'),
    onSuccess: onCompleted
  });

  const displayAlertMessage = (msg = '') => {
    setShowAlertMessage({ visibility: true, msg });
    const t = setTimeout(() => {
      setShowAlertMessage({ visibility: false, msg });
      clearTimeout(t);
    }, 5000);
  };

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue({
      patient: selectedTask?.patient_name,
      sub_type: selectedTask?.task_sub_type,
      case_selection: selectedTask?.case_selection,
      notes: selectedTask?.task_notes,
      created_by: selectedTask?.creator_name,
      assigned_by: selectedTask?.assigned_by,
      assigned_to: selectedTask?.assigned_to
    });
  }, []);

  return (
    <Modal
      title={
        selectedTask?.task_type && selectedTask?.client_case_id
          ? `${selectedTask.task_type} - Case ID: ${selectedTask.client_case_id}`
          : 'Task Type'
      }
      open={isClientAdmin}
      okText="Update"
      onCancel={onClose}
      data-testid="edit-task-modal"
      confirmLoading={isUpdateTaskLoading}
      width="798px"
      footer={[
        <Button data-testid="edit-task-cancel" key="back" onClick={onClose} style={{ fontWeight: '400' }}>
          {t('Cancel')}
        </Button>,
        <Button
          data-testid="edit-task-submit"
          key="submit"
          type="primary"
          onClick={() => {
            if (!assignedTo) {
              form.setFields([
                {
                  name: 'assigned_to',
                  errors: ['Please select a new referrer to assign']
                }
              ]);
            } else {
              updateTask();
            }
          }}
          style={{ fontWeight: '400' }}
        >
          {t('Update')}
        </Button>
      ]}
    >
      {/* Change title to task type */}
      <AlertMessage message={showAlertMessage.msg} visibility={showAlertMessage.visibility} />
      {form && (
        <Form
          form={form}
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 12 }}
          layout="vertical"
          style={{ margin: '12px 0%', width: '100%', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}
          initialValues={{
            patient: selectedTask?.patient_name,
            sub_type: selectedTask?.task_sub_type,
            case_selection: selectedTask?.case_selection,
            notes: selectedTask?.task_notes,
            created_by: selectedTask?.creator_name,
            assigned_by: selectedTask?.assigned_by,
            assigned_to: selectedTask?.assigned_to
          }}
        >
          {/* Patient */}
          <Col style={{ gridColumn: 'span 1' }}>
            <Form.Item name="patient" label={t('Patient')}>
              <Input placeholder={t('No Patient')} disabled />
            </Form.Item>
          </Col>
          {/* Task Sub Type */}
          <Col style={{ gridColumn: 'span 1' }}>
            <Form.Item name="sub_type" label={t('Task Sub Type')}>
              <Input placeholder={t('No Task Sub Type')} disabled />
            </Form.Item>
          </Col>
          {/* Case Selection */}
          <Col style={{ gridColumn: 'span 1' }}>
            <Form.Item name="case_selection" label={t('Case Selection')}>
              <Input placeholder={t('No Case Selection')} disabled />
            </Form.Item>
          </Col>
          {/* Date Created */}
          <Col style={{ gridColumn: 'span 1' }}>
            <Form.Item label={t('Date Created')}>
              {selectedTask?.created_date ? (
                <div style={{ width: '100%', backgroundColor: 'fefefe' }}>
                  <Datestamp
                    date={new Date(selectedTask?.created_date)}
                    format="Date @ Time (short, no year, 24h) - Fri, Mar 3rd @ 08:57"
                  />
                </div>
              ) : (
                <></>
              )}
            </Form.Item>
          </Col>
          {/* Date Due */}
          <Col style={{ gridColumn: 'span 2' }}>
            <Form.Item label={t('Due Date')}>
              {selectedTask?.due_date ? (
                <div style={{ width: '100%', backgroundColor: 'fefefe' }}>
                  <Datestamp
                    date={new Date(selectedTask?.due_date)}
                    format="Date @ Time (short, no year, 24h) - Fri, Mar 3rd @ 08:57"
                  />
                </div>
              ) : (
                <></>
              )}
            </Form.Item>
          </Col>
          {/* Created By */}
          <Col style={{ gridColumn: 'span 1' }}>
            <Form.Item name="created_by" label={t('Created By')}>
              <Input disabled />
            </Form.Item>
          </Col>
          {/* Assigned By */}
          <Col style={{ gridColumn: 'span 1' }}>
            <Form.Item name="assigned_by" label={t('Assigned By')}>
              <Input disabled />
            </Form.Item>
          </Col>
          {/* Assigned To */}
          <Col style={{ gridColumn: 'span 1' }}>
            <Form.Item name="assigned_to" label={t('Assigned To')}>
              <Select
                placeholder={t('Select a referrer to assign')}
                data-testid="edit-task-assign_to"
                loading={isTaskAssigneeLoading}
                style={{ width: '100%' }}
                onChange={(value, options) => {
                  setAssignedTo(String(value));
                }}
              >
                {taskAssignee?.data.assignee.map((assignee: any) => {
                  return (
                    <Select.Option
                      key={assignee.assignee_guid}
                      value={assignee.assignee_guid}
                      label={assignee.assignee}
                    >
                      {assignee.assignee}
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>
          </Col>

          {/* Notes */}
          <Col style={{ gridColumn: 'span 3' }}>
            <Form.Item name="notes" label={t('Notes')}>
              <Input.TextArea disabled />
            </Form.Item>
          </Col>

          {/* Select a referrer to assign */}
        </Form>
      )}
    </Modal>
  );
};

const EditTaskModal = ({ show, ...rest }: EditTaskModalProps) => show && <EditTaskModalContent {...rest} />;

export default EditTaskModal;
